/**
 * Adapter for the standard `robot.status` topic that robots publish over the data channel.
 *
 * Wire format (sdks/ketirtc/examples/temp_robot_status_msg.py):
 *   {
 *     robot_info:   { robot_id, sub_id, angle_unit, robot_type: { category, model },
 *                     capability: { common: [...], custom: [...] } },
 *     robot_state:  { base_state,
 *                     pose: { x, y, z, qx, qy, qz, qw },
 *                     velocity: { linear: [x, y, z], angular: [x, y, z], frame },
 *                     battery_state: { percentage, is_charging, voltage, current, temperature },
 *                     joint_state, raw_status, extension },
 *     mission_info: { mission_state, mission_name, current_task,
 *                     available_missions: [{ name, description }] }
 *   }
 *
 * `normalizeRobotStatus` keeps exactly that structure (sections missing on the wire become
 * null, numbers are coerced, lists default to []) and adds a `derived` block with the
 * display-ready values used by RobotStatusViewer and ROBOT_STATUS_COMPARE_KEYS.
 * The flat `robot_status` message of older robots is deprecated and not supported.
 */

import { servicesUrlConfig } from "shared/config/setting";

const toNumber = (value) => {
  const n = typeof value === "string" ? Number(value) : value;
  return typeof n === "number" && Number.isFinite(n) ? n : null;
};

const round = (value, digits = 2) => {
  if (value == null) return null;
  const factor = 10 ** digits;
  return Math.round(value * factor) / factor;
};

const isObject = (value) =>
  value != null && typeof value === "object" && !Array.isArray(value);

const toVector3 = (value) =>
  Array.isArray(value) ? [toNumber(value[0]), toNumber(value[1]), toNumber(value[2])] : null;

/**
 * Yaw (heading) in degrees from a quaternion, z-up convention (REP-103).
 * @returns {number|null} yaw in (-180, 180], or null when any component is missing
 */
export const quaternionToYawDeg = ({ qx, qy, qz, qw } = {}) => {
  const x = toNumber(qx);
  const y = toNumber(qy);
  const z = toNumber(qz);
  const w = toNumber(qw);
  if (x == null || y == null || z == null || w == null) return null;
  const yaw = Math.atan2(2 * (w * z + x * y), 1 - 2 * (y * y + z * z));
  return (yaw * 180) / Math.PI;
};

// ---------- robot_info ----------

const normalizeRobotInfo = (robotInfo) => {
  if (!isObject(robotInfo)) return null;
  const robotType = isObject(robotInfo.robot_type) ? robotInfo.robot_type : null;
  const capability = isObject(robotInfo.capability) ? robotInfo.capability : null;
  return {
    robot_id: robotInfo.robot_id ?? null,
    sub_id: robotInfo.sub_id ?? null,
    angle_unit: robotInfo.angle_unit ?? null,
    robot_type: robotType
      ? { category: robotType.category ?? null, model: robotType.model ?? null }
      : null,
    capability: capability
      ? {
          common: Array.isArray(capability.common) ? capability.common : [],
          custom: Array.isArray(capability.custom) ? capability.custom : [],
        }
      : null,
  };
};

// ---------- robot_state ----------

const normalizePose = (pose) =>
  isObject(pose)
    ? {
        x: toNumber(pose.x),
        y: toNumber(pose.y),
        z: toNumber(pose.z),
        qx: toNumber(pose.qx),
        qy: toNumber(pose.qy),
        qz: toNumber(pose.qz),
        qw: toNumber(pose.qw),
      }
    : null;

const normalizeVelocity = (velocity) =>
  isObject(velocity)
    ? {
        linear: toVector3(velocity.linear),
        angular: toVector3(velocity.angular),
        frame: velocity.frame ?? null,
      }
    : null;

const normalizeBatteryState = (batteryState) =>
  isObject(batteryState)
    ? {
        percentage: toNumber(batteryState.percentage), // 0..100
        is_charging: batteryState.is_charging == null ? null : Boolean(batteryState.is_charging),
        voltage: toNumber(batteryState.voltage),
        current: toNumber(batteryState.current),
        temperature: toNumber(batteryState.temperature),
      }
    : null;

const normalizeRobotState = (robotState) => {
  if (!isObject(robotState)) return null;
  return {
    base_state: robotState.base_state ?? null,
    pose: normalizePose(robotState.pose),
    velocity: normalizeVelocity(robotState.velocity),
    battery_state: normalizeBatteryState(robotState.battery_state),
    joint_state: isObject(robotState.joint_state) ? robotState.joint_state : null,
    raw_status: isObject(robotState.raw_status) ? robotState.raw_status : null,
    extension: isObject(robotState.extension) ? robotState.extension : null,
  };
};

// ---------- mission_info ----------

const normalizeMissionInfo = (missionInfo) =>
  isObject(missionInfo)
    ? {
        mission_state: missionInfo.mission_state ?? null,
        mission_name: missionInfo.mission_name ?? null,
        current_task: missionInfo.current_task ?? null,
        available_missions: Array.isArray(missionInfo.available_missions)
          ? missionInfo.available_missions
          : [],
      }
    : null;

// ---------- derived display values ----------

const deriveDisplayValues = (robotState) => {
  const percentage = robotState?.battery_state?.percentage ?? null;
  const linear = robotState?.velocity?.linear;
  const vx = linear?.[0] ?? null;
  const vy = linear?.[1] ?? 0;
  // Planar ground speed in m/s; the viewer's bar is a 0..100 % scale of 1 m/s.
  const speedMps = vx == null ? null : Math.hypot(vx, vy);
  const yaw = robotState?.pose ? quaternionToYawDeg(robotState.pose) : null;
  return {
    battery_percent:
      percentage == null ? null : Math.round(Math.max(0, Math.min(100, percentage))),
    speed_mps: speedMps == null ? null : round(speedMps, 2),
    speed_percent: speedMps == null ? null : Math.min(100, Math.round(speedMps * 100)),
    yaw_deg: yaw == null ? null : round(yaw, 1),
  };
};

/**
 * Normalize a `robot.status` topic value for the control pages.
 *
 * @param {Object} topicValue - `value` of the topic message
 * @returns {Object|null} { robot_info, robot_state, mission_info, derived }, or null when the
 *   payload is not a robot.status value (no standard section present)
 */
export const normalizeRobotStatus = (topicValue) => {
  if (!isObject(topicValue)) return null;

  const robotInfo = normalizeRobotInfo(topicValue.robot_info);
  const robotState = normalizeRobotState(topicValue.robot_state);
  const missionInfo = normalizeMissionInfo(topicValue.mission_info);
  if (!robotInfo && !robotState && !missionInfo) return null;

  return {
    robot_info: robotInfo,
    robot_state: robotState,
    mission_info: missionInfo,
    derived: deriveDisplayValues(robotState),
  };
};

/**
 * Dot-paths of the normalized status that the status panel renders; `useRobotStatus`
 * only re-renders when one of them changes (joint_state / raw_status / extension are
 * deliberately left out because they change on every tick).
 */
export const ROBOT_STATUS_COMPARE_KEYS = [
  "robot_state.base_state",
  "derived.battery_percent",
  "robot_state.battery_state.is_charging",
  "derived.speed_mps",
  "robot_state.pose.x",
  "robot_state.pose.y",
  "derived.yaw_deg",
  "mission_info.mission_state",
  "mission_info.mission_name",
  "mission_info.current_task",
  "robot_info.robot_type.model",
  "robot_info.sub_id",
];

export const getRobotIconPath = (robotInfo) => {
  return servicesUrlConfig.ROBOTS_API.getStaticUrl(
                      robotInfo?.icon_path ??
                      robotInfo?.robot_type?.icon_path ??
                      "/robot_type_icons/unknown_robot_type.png",
                    )
}


export default normalizeRobotStatus;
